import kimiK3Data from "./generated/kimi-k3.json";
import type { Benchmark, BenchmarkId, Comparability, LabId } from "../types";

export interface ReleaseChartPoint {
  modelId: string;
  value: number;
  displayValue: string;
  sourceLabel: string;
  note?: string;
}

export interface ReleaseChart {
  id: string;
  benchmarkId: BenchmarkId;
  title: string;
  shortName: string;
  category: Benchmark["category"];
  metric: string;
  unit: Benchmark["unit"];
  direction: Benchmark["direction"];
  comment?: string;
  points: ReleaseChartPoint[];
}

export interface ReleaseChartGroup {
  id: string;
  title: string;
  subtitle: string;
  benchmarkIds: BenchmarkId[];
  pointLimit?: number;
}

interface KimiPayload {
  generatedAt: string;
  source: {
    id: string;
    title: string;
    url: string;
    publishedAt: string;
  };
  chartGroups: ReleaseChartGroup[];
  benchmarks: Array<{
    id: BenchmarkId;
    name: string;
    shortName: string;
    category: Benchmark["category"];
    metric: string;
    unit: Benchmark["unit"];
    direction: Benchmark["direction"];
    comment: string;
    points: ReleaseChartPoint[];
  }>;
}

const kimiPayload = kimiK3Data as KimiPayload;

export const kimiK3Release = {
  ...kimiPayload.source,
  reporterId: "kimi" as LabId,
  modelId: "kimi-k3",
  generatedAt: kimiPayload.generatedAt,
};

export const kimiK3Charts: ReleaseChart[] = kimiPayload.benchmarks.map((benchmark) => ({
  id: `kimi-${benchmark.id}`,
  benchmarkId: benchmark.id,
  title: benchmark.name,
  shortName: benchmark.shortName,
  category: benchmark.category,
  metric: benchmark.metric,
  unit: benchmark.unit,
  direction: benchmark.direction,
  comment: benchmark.comment,
  points: benchmark.points,
}));

export const kimiK3ChartGroups = kimiPayload.chartGroups;

const grokPoint = (
  modelId: string,
  value: number,
  displayValue: string,
  sourceLabel: string,
): ReleaseChartPoint => ({ modelId, value, displayValue, sourceLabel });

export const grok45Charts: ReleaseChart[] = [
  {
    id: "grok-deep-swe-1-0",
    benchmarkId: "deep-swe-1-0",
    title: "DeepSWE 1.0",
    shortName: "DeepSWE 1.0",
    category: "Coding",
    metric: "pass@1",
    unit: "%",
    direction: "higher",
    comment: "Eval created by Datacurve, run with each model provider's harnesses by AA.",
    points: [
      grokPoint("fable-5", 66.1, "66.1%", "Fable max"),
      grokPoint("gpt-5-5", 64.31, "64.31%", "GPT 5.5 xhigh"),
      grokPoint("grok-4-5", 62, "62.0%", "Grok 4.5"),
      grokPoint("opus-4-8", 55.75, "55.75%", "Opus 4.8 max"),
      grokPoint("opus-4-7", 40.12, "40.12%", "Opus 4.7 max"),
    ],
  },
  {
    id: "grok-deep-swe-1-1",
    benchmarkId: "deep-swe",
    title: "DeepSWE 1.1",
    shortName: "DeepSWE 1.1",
    category: "Coding",
    metric: "pass@1",
    unit: "%",
    direction: "higher",
    comment: "mini-swe-agent harness run by Datacurve.",
    points: [
      grokPoint("fable-5", 70, "70%", "Fable max"),
      grokPoint("gpt-5-5", 67, "67%", "GPT 5.5 xhigh"),
      grokPoint("opus-4-8", 59, "59%", "Opus 4.8 max"),
      grokPoint("grok-4-5", 53, "53%", "Grok 4.5"),
      grokPoint("glm-5-2", 44, "44%", "GLM 5.2"),
    ],
  },
  {
    id: "grok-swe-marathon",
    benchmarkId: "swe-marathon",
    title: "SWE Marathon",
    shortName: "SWE Marathon",
    category: "Coding",
    metric: "resolution rate · pass@1",
    unit: "%",
    direction: "higher",
    points: [
      grokPoint("grok-4-5", 29, "29.0%", "Grok 4.5"),
      grokPoint("opus-4-8", 26, "26.0%", "Opus 4.8 max"),
      grokPoint("fable-5", 24, "24.0%", "Fable max"),
      grokPoint("opus-4-7", 16, "16.0%", "Opus 4.7 max"),
    ],
  },
  {
    id: "grok-terminal-bench",
    benchmarkId: "terminal-bench-2-1",
    title: "Terminal Bench 2.1",
    shortName: "Terminal Bench 2.1",
    category: "Coding",
    metric: "score",
    unit: "%",
    direction: "higher",
    points: [
      grokPoint("fable-5", 84.3, "84.3%", "Fable max"),
      grokPoint("gpt-5-5", 83.4, "83.4%", "GPT 5.5 xhigh"),
      grokPoint("grok-4-5", 83.3, "83.3%", "Grok 4.5"),
      grokPoint("opus-4-8", 78.9, "78.9%", "Opus 4.8 max"),
      grokPoint("opus-4-7", 78.9, "78.9%", "Opus 4.7 max"),
    ],
  },
  {
    id: "grok-swe-bench-pro",
    benchmarkId: "swe-bench-pro",
    title: "SWE Bench Pro",
    shortName: "SWE Bench Pro",
    category: "Coding",
    metric: "resolve rate",
    unit: "%",
    direction: "higher",
    points: [
      grokPoint("fable-5", 80.4, "80.4%", "Fable max"),
      grokPoint("opus-4-8", 69.2, "69.2%", "Opus 4.8 max"),
      grokPoint("grok-4-5", 64.7, "64.7%", "Grok 4.5"),
      grokPoint("opus-4-7", 64.3, "64.3%", "Opus 4.7 max"),
      grokPoint("glm-5-2", 62.1, "62.1%", "GLM 5.2"),
      grokPoint("gpt-5-5", 58.6, "58.6%", "GPT 5.5 xhigh"),
    ],
  },
];

export const grok45Efficiency: ReleaseChart = {
  id: "grok-token-efficiency",
  benchmarkId: "swe-bench-pro-output-tokens",
  title: "Token efficiency",
  shortName: "SWE Bench Pro output tokens",
  category: "Efficiency",
  metric: "average output tokens per SWE Bench Pro task",
  unit: "tokens",
  direction: "lower",
  comment: "Grok 4.5 uses about 4.2× fewer output tokens in the reported comparison.",
  points: [
    grokPoint("grok-4-5", 15_954, "15,954", "Grok 4.5"),
    grokPoint("opus-4-8", 67_020, "67,020", "Opus 4.8 (max)"),
  ],
};

const existingBenchmarkIds = new Set<BenchmarkId>([
  "deep-swe",
  "swe-bench-pro",
  "terminal-bench-2-1",
  "swe-marathon",
  "hle",
  "hle-tools",
]);

const benchmarkDescription = (chart: ReleaseChart) => {
  const source = chart.id.startsWith("kimi-") ? "Kimi K3" : "Grok 4.5";
  return `${source} release comparison. Protocol, harness, and cited-value caveats remain attached to the reporting source.`;
};

export const latestBenchmarkDefinitions: Benchmark[] = [
  ...kimiK3Charts
    .filter((chart) => !existingBenchmarkIds.has(chart.benchmarkId))
    .map((chart) => ({
      id: chart.benchmarkId,
      name: chart.title,
      shortName: chart.shortName,
      category: chart.category,
      metric: chart.metric,
      unit: chart.unit,
      direction: chart.direction,
      description: benchmarkDescription(chart),
    })),
  {
    id: "deep-swe-1-0",
    name: "DeepSWE 1.0",
    shortName: "DeepSWE 1.0",
    category: "Coding",
    metric: "pass@1",
    unit: "%",
    direction: "higher",
    description: benchmarkDescription(grok45Charts[0]),
  },
  {
    id: grok45Efficiency.benchmarkId,
    name: "SWE Bench Pro output-token efficiency",
    shortName: "SWE Pro output tokens",
    category: "Efficiency",
    metric: grok45Efficiency.metric,
    unit: "tokens",
    direction: "lower",
    description: benchmarkDescription(grok45Efficiency),
  },
];

export interface ClaimSeed {
  benchmarkId: BenchmarkId;
  modelId: string;
  score: number;
  displayValue: string;
  locator: string;
  comparability: Comparability;
  note?: string;
}

export const kimiK3ClaimSeeds: ClaimSeed[] = kimiK3Charts.flatMap((chart) =>
  chart.points.map((point) => ({
    benchmarkId: chart.benchmarkId,
    modelId: point.modelId,
    score: point.value,
    displayValue: point.displayValue,
    locator:
      chart.comment === "Internal Knowledge Work Bench"
        ? `Internal Knowledge Work Bench · ${chart.title}`
        : `Full Benchmark Table · ${chart.title}`,
    comparability: "method-sensitive",
    note: [chart.comment, point.note].filter(Boolean).join(" · ") || undefined,
  })),
);

export const grok45NewClaimSeeds: ClaimSeed[] = [
  ...grok45Charts[0].points.map((point) => ({
    benchmarkId: grok45Charts[0].benchmarkId,
    modelId: point.modelId,
    score: point.value,
    displayValue: point.displayValue,
    locator: "DeepSWE 1.0 chart",
    comparability: "method-sensitive" as const,
    note: grok45Charts[0].comment,
  })),
  ...grok45Efficiency.points.map((point) => ({
    benchmarkId: grok45Efficiency.benchmarkId,
    modelId: point.modelId,
    score: point.value,
    displayValue: point.displayValue,
    locator: "Token efficiency figure",
    comparability: "same-table" as const,
    note: grok45Efficiency.comment,
  })),
];
